import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoDateInput } from "@redotech/redo-web/arbiter-components/input/redo-date-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useContext, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { useHasMarketingPermission } from "../../../marketing/use-marketing-permissions";
import { TeamContext } from "../../team";

type ItemStatus = "pending" | "imported" | "conflict";

interface MigrationItem {
  label: string;
  detail?: string;
  status: ItemStatus;
  conflictReason?: string;
}

const statusStyles: Record<
  ItemStatus,
  { backgroundColor: string; color: string; text: string }
> = {
  pending: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
    text: "NOT IMPORTED",
  },
  imported: { backgroundColor: "#e6f4ea", color: "#1b7a3d", text: "IMPORTED" },
  conflict: { backgroundColor: "#fce8e6", color: "#c5221f", text: "CONFLICT" },
};

interface InvoiceBillingDisplay {
  startDate?: Date;
  period: string;
  periodFeeInDollars: number;
  includedShipments?: number;
  overagePricePerShipmentInDollars?: number;
}

function formatDateShort(date: Date): string {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatInvoiceBillingDetail(
  ib: InvoiceBillingDisplay | undefined,
): string | undefined {
  if (!ib) {
    return undefined;
  }
  const parts: string[] = [];
  parts.push(
    ib.startDate ? `Starts ${formatDateShort(ib.startDate)}` : "No start date",
  );
  parts.push(ib.period);
  parts.push(`$${ib.periodFeeInDollars.toLocaleString()} period fee`);
  if (ib.includedShipments != null) {
    parts.push(`${ib.includedShipments.toLocaleString()} included shipments`);
  }
  if (ib.overagePricePerShipmentInDollars != null) {
    parts.push(
      `$${ib.overagePricePerShipmentInDollars.toLocaleString()} overage`,
    );
  }
  return parts.join(" · ");
}

function StatusBadge({ status }: { status: ItemStatus }) {
  const style = statusStyles[status];
  return (
    <Text
      as="span"
      fontWeight="semibold"
      style={{
        display: "inline-block",
        padding: "var(--redo-spacing-xxs) var(--redo-spacing-md)",
        borderRadius: "var(--border-radius-sm)",
        fontSize: "var(--redo-font-size-xs)",
        backgroundColor: style.backgroundColor,
        color: style.color,
      }}
    >
      {style.text}
    </Text>
  );
}

export const MalomoMigrationCard = memo(function MalomoMigrationCard() {
  const isAuthorized = useHasMarketingPermission("marketing_admin");
  const team = useContext(TeamContext);
  const rpcClient = useContext(RedoAdminRpcClientContext);
  const queryClient = useQueryClient();
  const [startDateOverride, setStartDateOverride] =
    useState<Temporal.PlainDate | null>(null);

  const { data: status, isLoading } = useQuery({
    queryKey: ["malomoMigrationStatus", team?._id],
    queryFn: () => {
      if (!team || !rpcClient) {
        throw new Error("Team or RPC client not available");
      }
      return rpcClient.getMalomoMigrationStatus({ teamId: team._id });
    },
    enabled: !!team && !!rpcClient,
  });

  const applyMutation = useMutation({
    mutationFn: async (startDateOverrideArg?: string) => {
      if (!team || !rpcClient) {
        throw new Error("Team or RPC client not available");
      }
      return rpcClient.applyMalomoMigration({
        teamId: team._id,
        startDateOverride: startDateOverrideArg,
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        const parts: string[] = [];
        if (result.carrierMappingsImported > 0) {
          parts.push(
            `${result.carrierMappingsImported} carrier mappings imported`,
          );
        }
        if (result.stalledSettingsImported) {
          parts.push("stalled settings imported");
        }
        if (result.integrationsCreated.length > 0) {
          parts.push(
            `integrations created: ${result.integrationsCreated.join(", ")}`,
          );
        }
        if (result.billingImported) {
          parts.push("invoice billing imported");
        }
        toast(
          parts.length > 0
            ? `Migration applied: ${parts.join("; ")}`
            : "Migration applied (nothing new to import)",
          { variant: "success" },
        );
        if (result.billingStartDateOmitted) {
          toast(
            "Invoice billing start date was in the past and left blank — set a future start date in the billing settings.",
            { variant: "warning" },
          );
        }
        void queryClient.invalidateQueries({
          queryKey: ["malomoMigrationStatus", team?._id],
        });
      } else {
        toast(result.error || "Migration failed", { variant: "error" });
      }
    },
    onError: (error) => {
      toast(
        `Migration failed: ${error instanceof Error ? error.message : "Unknown error"}`,
        { variant: "error" },
      );
    },
  });

  if (isLoading || !status?.found) {
    return null;
  }

  const items: MigrationItem[] = [];

  if (status.hasCarrierMappings) {
    items.push({
      label: "Carrier Mappings",
      detail: `${status.carrierMappingsCount} mappings`,
      status: status.carrierMappingsAlreadyImported ? "imported" : "pending",
    });
  }
  if (status.hasStalledSettings) {
    items.push({
      label: "Stalled Settings",
      detail: "Shipment + fulfillment stall detection",
      status: status.stalledSettingsAlreadyImported ? "imported" : "pending",
    });
  }
  for (const integration of status.integrations) {
    items.push({
      label: `${integration.kind.charAt(0).toUpperCase()}${integration.kind.slice(1)} Integration`,
      detail: integration.connectionKind,
      status: integration.status,
      conflictReason: integration.conflictReason,
    });
  }
  if (status.hasInvoiceBilling) {
    items.push({
      label: "Invoice Billing",
      detail: formatInvoiceBillingDetail(status.invoiceBilling),
      status: status.invoiceBillingAlreadyImported ? "imported" : "pending",
    });
  }

  const showStartDateOverride =
    status.hasInvoiceBilling && !status.invoiceBillingAlreadyImported;
  const sourceStartDate = status.invoiceBilling?.startDate;
  const overrideIsPast =
    startDateOverride != null &&
    Temporal.PlainDate.compare(startDateOverride, Temporal.Now.plainDateISO()) <
      0;

  return (
    <Card title="Import Malomo Settings">
      <Flex dir="column" gap="lg">
        <Text fontSize="sm">
          Found Malomo settings for{" "}
          <strong>{status.merchantName ?? status.shopifyDomain}</strong>
        </Text>

        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "var(--border-radius-lg)",
            overflow: "hidden",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "var(--redo-font-size-sm)",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "var(--redo-colors-gray-neutral-50)",
                }}
              >
                <th
                  style={{
                    textAlign: "left",
                    padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
                    borderBottom: "1px solid #e5e7eb",
                    fontWeight: "var(--redo-font-weight-semibold)",
                  }}
                >
                  Setting
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
                    borderBottom: "1px solid #e5e7eb",
                    fontWeight: "var(--redo-font-weight-semibold)",
                  }}
                >
                  Details
                </th>
                <th
                  style={{
                    textAlign: "center",
                    padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
                    borderBottom: "1px solid #e5e7eb",
                    fontWeight: "var(--redo-font-weight-semibold)",
                  }}
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr
                  key={item.label}
                  style={{
                    borderBottom:
                      i < items.length - 1 ? "1px solid #e5e7eb" : undefined,
                    backgroundColor:
                      item.status === "conflict" ? "#fef7f6" : undefined,
                  }}
                >
                  <td
                    style={{
                      padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
                      fontWeight: "var(--redo-font-weight-medium)",
                    }}
                  >
                    {item.label}
                  </td>
                  <td
                    style={{
                      padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
                      color: "var(--redo-colors-text-text-tertiary)",
                    }}
                  >
                    {item.status === "conflict" && item.conflictReason ? (
                      <Text as="span" style={{ color: "#c5221f" }}>
                        {item.conflictReason}
                      </Text>
                    ) : (
                      item.detail
                    )}
                  </td>
                  <td
                    style={{
                      padding: "var(--redo-spacing-md) var(--redo-spacing-lg)",
                      textAlign: "center",
                    }}
                  >
                    <StatusBadge status={item.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {status.hasAnythingToImport ? (
          <Flex align="flex-start" dir="column" gap="md">
            {showStartDateOverride && (
              <RedoDateInput
                description={
                  sourceStartDate
                    ? `Optional. Overrides the Malomo invoice billing start date (default ${formatDateShort(sourceStartDate)}). Leave blank to use it.`
                    : "Optional invoice billing start date. The Malomo record has no start date."
                }
                errorText={
                  overrideIsPast
                    ? "This date is in the past; it will be left blank on import."
                    : undefined
                }
                fullWidth
                label="Invoice billing start date override"
                setValue={setStartDateOverride}
                value={startDateOverride}
              />
            )}
            <RedoButton
              disabled={!isAuthorized || applyMutation.isPending}
              hierarchy="primary"
              onClick={() =>
                applyMutation.mutate(
                  startDateOverride ? startDateOverride.toString() : undefined,
                )
              }
              size="sm"
              text={
                applyMutation.isPending
                  ? "Importing..."
                  : "Import Malomo settings"
              }
            />
          </Flex>
        ) : (
          <Text fontSize="sm" textColor="secondary">
            All Malomo settings have already been imported.
          </Text>
        )}
      </Flex>
    </Card>
  );
});
